import React, { useCallback, useState } from "react";
import TextEditor from "../../common/TextEditor/TextEditor";
import { CAPA_ACTION_PLANNING_EMAIL_MERGE_TAGS } from "./capaActionPlanningEmailTags";
import "./CapaActionPlanningEmailConfigEditor.scss";

async function copyMergeTagToClipboard(tag) {
  const text = String(tag || "");
  if (!text) return false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_e) {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_e2) {
    return false;
  }
}

/**
 * Two-column layout: subject + rich body (left), merge-tag list (right) from lg breakpoint.
 * fillAvailableHeight stretches the block to fill the parent so editor + tags scroll inside the viewport.
 */
export default function CapaActionPlanningEmailConfigEditor({
  subjectTemplate,
  onSubjectChange,
  bodyTemplate,
  onBodyChange,
  subjectDisabled = false,
  bodyDisabled = false,
  onTagCopied,
  description,
  mergeTags = CAPA_ACTION_PLANNING_EMAIL_MERGE_TAGS,
  /** Stretch columns to the same height and scroll inside (fit viewport with parent footer). */
  fillAvailableHeight = true,
  /** Block document deep links / Open document merge tags (e.g. QMS remove-access template). */
  forbidDocumentLinks = false,
  /** Block pasted/dropped images in the body editor. Default true for email templates. */
  blockImages = true,
  validateBodyContent,
  validateSubjectContent,
  onForbiddenContent,
}) {
  const [lastCopied, setLastCopied] = useState("");

  const subjectValidator = validateSubjectContent;

  const notifyBlocked = useCallback(
    (message, field = "body") => {
      onForbiddenContent?.({ message, field });
    },
    [onForbiddenContent],
  );

  const handleSubjectPaste = useCallback(
    (e) => {
      const text = e.clipboardData?.getData("text/plain") || "";
      const html = e.clipboardData?.getData("text/html") || "";
      const probe = html.trim() ? html : text;
      if (subjectValidator) {
        const result = subjectValidator(probe);
        if (result && result.allowed === false) {
          e.preventDefault();
          notifyBlocked(result.message || "This content is not allowed.", "subject");
          return;
        }
      }
      if (blockImages && (/<img\b/i.test(probe) || /data:image\//i.test(probe))) {
        e.preventDefault();
        notifyBlocked("Images cannot be pasted into email templates.", "subject");
      }
    },
    [subjectValidator, notifyBlocked, blockImages],
  );

  const handleSubjectChange = useCallback(
    (e) => {
      const next = e.target.value;
      if (subjectValidator) {
        const result = subjectValidator(next);
        if (result && result.allowed === false) {
          notifyBlocked(result.message || "This content is not allowed.", "subject");
          return;
        }
      }
      onSubjectChange?.(next);
    },
    [onSubjectChange, subjectValidator, notifyBlocked],
  );

  const handleBodyBlocked = useCallback(
    (message) => {
      notifyBlocked(message || "This content is not allowed.", "body");
    },
    [notifyBlocked],
  );

  const handleCopyTag = useCallback(
    async (tag) => {
      if (subjectValidator) {
        const result = subjectValidator(String(tag || ""));
        if (result && result.allowed === false) {
          notifyBlocked(result.message || "This merge tag is not allowed here.", "subject");
          return;
        }
      }
      const ok = await copyMergeTagToClipboard(tag);
      if (ok) {
        setLastCopied(tag);
        onTagCopied?.(tag);
        window.setTimeout(() => setLastCopied(""), 2000);
      }
    },
    [onTagCopied, subjectValidator, notifyBlocked],
  );

  const rootClass = [
    "capa-action-planning-email-config",
    fillAvailableHeight ? "capa-action-planning-email-config--fill" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass}>
      {description ? (
        <p className="text-muted small mb-2 capa-ap-email-desc flex-shrink-0">{description}</p>
      ) : null}

      <div className="capa-ap-email-columns">
        <div className="capa-ap-email-col capa-ap-email-col--editor">
          <div className="capa-ap-email-subject flex-shrink-0 mb-2">
            <label className="form-label capa-email-config-label">Subject template</label>
            <input
              className="form-control capa-email-config-input"
              value={subjectTemplate ?? ""}
              onChange={handleSubjectChange}
              onPaste={handleSubjectPaste}
              disabled={subjectDisabled}
              spellCheck={false}
            />
          </div>
          <div className="capa-ap-email-editor-body-slot">
            <label className="form-label capa-email-config-label">Body template (rich text)</label>
            <TextEditor
              value={bodyTemplate ?? ""}
              onChange={onBodyChange}
              placeholder="Email body…"
              height={fillAvailableHeight ? "100%" : "18rem"}
              minHeight="12rem"
              showImageButton={false}
              blockImages={blockImages}
              showLinkButton={!forbidDocumentLinks}
              disabled={bodyDisabled}
              className="capa-ap-email-quill-root"
              validateContentBeforeApply={validateBodyContent}
              onContentBlocked={handleBodyBlocked}
            />
          </div>
        </div>

        <div className="capa-ap-email-col capa-ap-email-col--tags">
          <div className="capa-email-tags-card border rounded-3 h-100 capa-email-tags-card-inner capa-email-tags-card--compact">
            <div className="capa-email-tags-card__header d-flex justify-content-between align-items-center flex-shrink-0">
              <span className="capa-email-config-label small mb-0">Merge tags</span>
              {lastCopied ? (
                <span className="capa-email-tags-card__hint text-success">
                  <i className="fas fa-check me-1" aria-hidden={true} />
                  Copied
                </span>
              ) : (
                <span className="capa-email-tags-card__hint text-muted">Click tag or copy</span>
              )}
            </div>
            <div className="capa-email-tag-list flex-grow-1 min-h-0" role="list">
              {mergeTags.map((row) => (
                <div key={row.tag} className="capa-email-tag-item" role="listitem">
                  <div className="capa-email-tag-item__top">
                    <button
                      type="button"
                      className="capa-email-merge-tag capa-email-merge-tag--item"
                      title={`Copy ${row.tag}`}
                      onClick={() => handleCopyTag(row.tag)}
                    >
                      {row.tag}
                    </button>
                    <button
                      type="button"
                      className="capa-email-tag-item__copy-btn"
                      title={`Copy ${row.tag}`}
                      aria-label={`Copy ${row.tag}`}
                      onClick={() => handleCopyTag(row.tag)}
                    >
                      <i className="fas fa-copy" aria-hidden />
                    </button>
                  </div>
                  <div className="capa-email-tag-item__meta">
                    <span className="capa-email-tag-item__entity">{row.entityName}</span>
                    <span className="capa-email-tag-item__desc">{row.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
